// App shell layout. Replaces the old hand-rolled CSS grid with an MUI-based
// responsive shell (permanent/mini Drawer sidebar + fixed AppBar topbar +
// scrollable content region).
//
// The public exports (DashGridL1, DashGridL2, and the *Container helpers) keep
// the SAME signatures the ~44 consuming pages already use, so no page needs to
// change. Pages pass their own Top/Middle/Main components; those render inside
// the content area, beneath the global topbar.

import React, { useState } from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";

import Sidebar, { DRAWER_WIDTH, MINI_WIDTH } from "_shared/Nav/Sidebar";
import Topbar from "_shared/Top/Topbar";

const COLLAPSE_KEY = "ui-sidebar-collapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

// Content-slot helpers kept for backward compatibility. They are now light
// wrappers with sensible spacing instead of CSS-grid sections.
export const TopContainer = ({ children }) => <Box sx={{ mb: 2 }}>{children}</Box>;
export const MiddleContainer = ({ children }) => <Box sx={{ mb: 2 }}>{children}</Box>;
export const MainContainer = ({ children }) => <Box>{children}</Box>;
export const DetailsContainer = ({ children }) => <Box>{children}</Box>;
export const RightContainer = ({ children }) => <Box>{children}</Box>;

// The shared shell: sidebar + topbar + scrollable content.
const AppShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapse = () =>
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch (e) {
        /* ignore */
      }
      return next;
    });

  const sidebarWidth = collapsed ? MINI_WIDTH : DRAWER_WIDTH;

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "background.default" }}>
      <Topbar
        onToggleMobile={() => setMobileOpen((v) => !v)}
        onToggleCollapse={toggleCollapse}
        appBarSx={{
          width: { md: `calc(100% - ${sidebarWidth}px)` },
          ml: { md: `${sidebarWidth}px` },
          color: "text.primary",
        }}
      />

      <Sidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        <Toolbar /> {/* spacer for the fixed AppBar */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: { xs: 2, md: 3 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

// Layout with Top + Middle + Main slots.
export const DashGridL1 = ({ Top, Middle, Main }) => (
  <AppShell>
    {Top && (
      <TopContainer>
        <Top />
      </TopContainer>
    )}
    {Middle && (
      <MiddleContainer>
        <Middle />
      </MiddleContainer>
    )}
    {Main && (
      <MainContainer>
        <Main />
      </MainContainer>
    )}
  </AppShell>
);

// Layout with Top + Main slots (most common).
export const DashGridL2 = ({ Top, Main }) => (
  <AppShell>
    {Top && (
      <TopContainer>
        <Top />
      </TopContainer>
    )}
    {Main && (
      <DetailsContainer>
        <Main />
      </DetailsContainer>
    )}
  </AppShell>
);

const DashGrid = ({ children }) => <AppShell>{children}</AppShell>;

export default DashGrid;
